import { z } from 'zod'

type CsvIngestionStatusDetailsError = {
  error_type: string
  field: string
  line_number: number
  txn_id: string
  value: string
}

type CsvIngestionStatusDetailsErrorSummary = Record<string, number>

export type CsvIngestionStatusDetails = {
  errors?: CsvIngestionStatusDetailsError[]
  error_summary?: CsvIngestionStatusDetailsErrorSummary
  invalid_rows: number
  skipped_rows: number
  total_rows: number
  valid_rows: number
  error_count?: number
}

type CsvIngestionStatus = 'MALFORMED' | 'COMPLETE'

export enum CsvIngestionStatusEnum {
  Malformed = 'MALFORMED',
  Complete = 'COMPLETE',
}

const csvIngestionStatusSchema = z.enum(['MALFORMED', 'COMPLETE'])

const csvIngestionStatusDetailsErrorSchema = z.object({
  error_type: z.string(),
  field: z.string(),
  line_number: z.number(),
  txn_id: z.string(),
  value: z.string(),
})

const csvIngestionStatusDetailsErrorSummarySchema = z.record(z.number())

const customErpCsvIngestionStatusDetailsSchema = z.object({
  errors: z.array(csvIngestionStatusDetailsErrorSchema).optional(),
  error_summary: csvIngestionStatusDetailsErrorSummarySchema.optional(),
  invalid_rows: z.number(),
  skipped_rows: z.number(),
  total_rows: z.number(),
  valid_rows: z.number(),
  error_count: z.number().optional(),
})

const regularCsvIngestionStatusDetailsSchema = z.object({
  errors: z.array(csvIngestionStatusDetailsErrorSchema).optional(),
  error_summary: csvIngestionStatusDetailsErrorSummarySchema.optional(),
  invalid_txns: z.number(),
  skipped_txns: z.number(),
  total_txns: z.number(),
  valid_txns: z.number(),
  error_count: z.number().optional(),
})

export type CsvIngestion = {
  name: string
  status?: CsvIngestionStatus
  status_details?: CsvIngestionStatusDetails
  created_at?: string
}

const csvIngestionSchema = z.object({
  name: z.string(),
  status: csvIngestionStatusSchema.optional(),
  status_details: customErpCsvIngestionStatusDetailsSchema.optional(),
  created_at: z.string().optional(),
})

type Ingestion = {
  imports?: CsvIngestion[]
}

export type FetchCsvIngestionsResponse = {
  ingestions?: Ingestion[]
}

const ingestionSchema = z.object({
  imports: z.array(csvIngestionSchema),
})

export const erpAccountsCsvResponseSchema = z.object({
  ingestions: ingestionSchema,
})

export const erpAccountsCsvByNameResponseSchema = z.object({
  csv: z.string(),
})

export const customErpCsvUploadResponseSchema = z.object({
  actual_start_cursor: z.string().nullable(),
  created_at: z.string(),
  end_cursor: z.string().nullable(),
  full_sync_id: z.string().nullable(),
  id: z.number(),
  integration: z.string(),
  name: z.string(),
  requested_start_cursor: z.string().nullable(),
  status: csvIngestionStatusSchema,
  status_details: customErpCsvIngestionStatusDetailsSchema,
  storage_type: z.string(),
})

export const regularCsvUploadResponseSchema = z.object({
  actual_start_cursor: z.string().nullable(),
  created_at: z.string(),
  end_cursor: z.string().nullable(),
  full_sync_id: z.string().nullable(),
  id: z.number(),
  integration: z.string(),
  name: z.string(),
  requested_start_cursor: z.string().nullable(),
  status: csvIngestionStatusSchema,
  status_details: regularCsvIngestionStatusDetailsSchema,
  storage_type: z.string(),
})

export type CustomErpCsvUploadResponse = {
  actual_start_cursor: string | null
  created_at: string
  end_cursor: string | null
  full_sync_id: string | null
  id: number
  integration: string
  name: string
  requested_start_cursor: string | null
  status: CsvIngestionStatus
  status_details: CsvIngestionStatusDetails
  storage_type: string
}
